/** AssistantPane hooks -- data fetching, connection monitoring, scrolling. All conversation HTTP calls go through the shared client with the direct storage-gateway transport (the chats SPA has no Next internal proxy). */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { REDUCER_ACTION_TYPES } from '@federa/const/reducer-actions';
import { MESSAGE_BATCH_CACHE_LIMIT, MESSAGE_BATCH_SIZE, NEWEST_GROUP_ONLY } from '@federa/const/do-storage-keys';
import { DEFAULT_GUEST_ROLE } from '@federa/const/auth-session';
import { isNonEmptyString } from '@federa/const/string-guards';
import { fetchConversations, fetchRecentMessages } from '@federa/components/messaging/messaging';
import { secureLogger } from '@federa/utils/deps/registry';
import { mapInBatches } from '@federa/utils/async/batch-processor';
import { MAP_IN_BATCHES_DEFAULT_CONCURRENCY } from '@federa/const/concurrency-caps';
import { LOG_EVENTS } from '@federa/const/logging';
import { recordStringOr } from '@federa/const/record-guards';
import { DOM_EVENTS } from '@federa/const/websocket-actions-dom';
import { ASSISTANT_ROUTE_KEEP_ALIVE_KEY } from '@federa/const/assistant';
import { isFreshAssistantRoom } from './assistant-room-freshness.ts';
import { buildAlexTransport } from './transport.ts';
import { uploadChatFile } from './attachments.ts';
import { sortRooms } from './useMessagingWebsocket.helpers.ts';
import type { RefObject, Dispatch, SetStateAction, MutableRefObject } from 'react';
import type { ChatMessageStruct } from '@federa/types/conversations-chat-structs';
import type {
	AssistantComputedValues as ComputedValues,
	DataFetchingConfig,
	MessageHistoryPaging,
	FileUploadMetadata,
	FileUploadParams,
	UseComputedValuesOptions,
} from '@federa/types/ui-chat-messaging';

export function useComputedValues(options: UseComputedValuesOptions): ComputedValues {
	const { currentConversation, currentMessages, selectedFiles, input, wsConnected, chatRoomList } = options;
	const displayMessages = useMemo(() => {
		if (!currentConversation) return [];
		return currentMessages.filter((m) => m.roomId === currentConversation.id);
	}, [currentMessages, currentConversation]);

	const uploadProgress = useMemo(() => {
		if (selectedFiles.length === 0) return 0;
		const totalProgress = selectedFiles.reduce((sum, file) => sum + (file.progress ?? 0), 0);
		return Math.round(totalProgress / (selectedFiles.length || 1));
	}, [selectedFiles]);

	const isUploading = useMemo(() => {
		return selectedFiles.some((file) => file.uploading === true);
	}, [selectedFiles]);

	const canSend = useMemo(() => {
		const hasContent = isNonEmptyString(input) || selectedFiles.length > 0;
		return hasContent && Boolean(currentConversation) && wsConnected && !isUploading;
	}, [input, selectedFiles.length, currentConversation, wsConnected, isUploading]);

	const sortedConversations = useMemo(() => {
		return sortRooms(chatRoomList);
	}, [chatRoomList]);

	return {
		displayMessages,
		uploadProgress,
		isUploading,
		canSend,
		sortedConversations,
	};
}

function runCancellableFetch<T>(fetchFn: () => Promise<T>, onSuccess: (result: T) => void, onError: (err: unknown) => void): () => void {
	let cancelled = false;
	fetchFn()
		.then((result) => {
			if (!cancelled) onSuccess(result);
		})
		.catch((err) => {
			if (!cancelled) onError(err);
		});
	return () => {
		cancelled = true;
	};
}

function useConversationsFetch(config: DataFetchingConfig): void {
	const { userId, role, canSwitchConversations, dispatch } = config;
	useEffect(() => {
		if (!userId || !canSwitchConversations) return;
		return runCancellableFetch(
			async () => fetchConversations({ userId, role: role || DEFAULT_GUEST_ROLE }, await buildAlexTransport(userId, undefined)),
			(result) => {
				// `ApiResponse` is literal-tagged and its success arm always carries `data`, so the TAG alone is the guard.
				if (result.success)
					dispatch({
						type: REDUCER_ACTION_TYPES.MESSAGING_SET_ROOMS,
						payload: result.data,
					});
			},
			(err) => secureLogger.error(LOG_EVENTS.ASSISTANT_PANE_CONVERSATIONS_FETCH_FAILED, { error: err }),
		);
	}, [userId, role, canSwitchConversations, dispatch]);
}

/**
 * A page came back SHORT of the groups it asked for, so there is nothing older to fetch.
 *
 * The ChatRoom cache serves whole groups of `MESSAGE_BATCH_SIZE`, so a response holding fewer items than the request
 * could have filled is the start of the history. `MESSAGE_BATCH_CACHE_LIMIT` is the other stop: past it the cache
 * evicts, and this client has no deeper reader.
 */
function hasOlderAfter(groups: number, received: number): boolean {
	return received >= groups * MESSAGE_BATCH_SIZE && groups < MESSAGE_BATCH_CACHE_LIMIT;
}

/**
 * The conversation's history: the newest group on open, older groups on demand.
 *
 * ONE GROUP ON OPEN, not the ten this used to ask for. The read behind it is now the ChatRoom DO's read-through cache,
 * which walks back from the newest group -- so asking for ten up front paid for nine groups nobody had scrolled to yet,
 * on every open of every conversation.
 *
 * "OLDER" IS A WIDER REQUEST, NOT AN OFFSET ONE. `fetchRecentMessages` walks backward from the newest group and returns
 * the whole window, so paging back means asking for one more group and replacing the list. That re-reads groups already
 * on screen, and it is still the right shape here: the window is capped at `MESSAGE_BATCH_CACHE_LIMIT` groups of DO
 * storage with no database read behind it, and an offset parameter would be a second way to address history for one
 * client's scrollback -- a cursor to keep in step with the walk, for a bounded list.
 *
 * THE INITIAL FETCH IS SKIPPED when the conversation already has messages, which is the socket's own backfill having
 * arrived first. That leg asks for the same one group through the same flow, so the two agree by construction.
 */
function useMessageHistory(config: DataFetchingConfig): MessageHistoryPaging {
	const { userId, currentConversation, currentMessages, dispatch, messagesEndRef } = config;
	const [loadedGroups, setLoadedGroups] = useState(NEWEST_GROUP_ONLY);
	const [hasOlderMessages, setHasOlderMessages] = useState(false);
	const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);
	const conversationId = currentConversation?.id;
	// The scroll geometry measured immediately BEFORE a scrollback page is dispatched; null at every other moment.
	const scrollAnchorRef = useRef<{ scrollHeight: number; scrollTop: number } | null>(null);

	/**
	 * Hold the reader's place across a prepend.
	 *
	 * Fifty messages appear ABOVE the viewport and the browser keeps `scrollTop` unchanged, which means the reader is
	 * now looking at a different part of the conversation than the one they were reading -- and because reaching the
	 * load-more control puts them at `scrollTop: 0`, they land at the top of the batch they just fetched rather than
	 * where they left off. The browser's own scroll anchoring does not cover this: it is suppressed at scroll offset
	 * zero, which is exactly the position this control is pressed from.
	 *
	 * The correction is the height the list GREW BY, applied to the old offset. `useLayoutEffect`, not `useEffect`, so
	 * it lands in the same frame as the paint -- a passive effect here would show the jump and then undo it.
	 */
	useLayoutEffect(() => {
		const anchor = scrollAnchorRef.current;
		if (!anchor) return;
		scrollAnchorRef.current = null;
		const container = findScrollParent(messagesEndRef.current);
		if (!container) return;
		container.scrollTop = container.scrollHeight - anchor.scrollHeight + anchor.scrollTop;
	}, [currentMessages, messagesEndRef]);

	// A different conversation is a different history: without this the next one inherits the previous one's scrollback
	// depth and its "there is more" answer, and asks for groups it has never shown.
	useEffect(() => {
		setLoadedGroups(NEWEST_GROUP_ONLY);
		setHasOlderMessages(false);
		setLoadingOlderMessages(false);
	}, [conversationId]);

	useEffect(() => {
		// A room minted by this very open has nothing to page (`isFreshAssistantRoom`); the socket backfill covers it.
		if (
			!currentConversation ||
			isFreshAssistantRoom(currentConversation) ||
			currentMessages.some((m) => m.roomId === currentConversation.id)
		)
			return;
		return runCancellableFetch(
			async () =>
				fetchRecentMessages(currentConversation.id, { batchCount: NEWEST_GROUP_ONLY }, await buildAlexTransport(userId, undefined)),
			(result) => {
				if (!result.success) return;
				setHasOlderMessages(hasOlderAfter(NEWEST_GROUP_ONLY, result.data.length));
				if (result.data.length > 0)
					dispatch({
						type: REDUCER_ACTION_TYPES.MESSAGING_SET_MESSAGES,
						payload: result.data,
					});
			},
			(err) =>
				secureLogger.error(LOG_EVENTS.ASSISTANT_PANE_MESSAGES_FETCH_FAILED, {
					error: err,
				}),
		);
	}, [userId, currentConversation, currentMessages, dispatch]);

	const loadOlderMessages = useCallback(() => {
		if (!conversationId || loadingOlderMessages || !hasOlderMessages) return;
		const nextGroups = loadedGroups + 1;
		setLoadingOlderMessages(true);
		void (async () => {
			try {
				const result = await fetchRecentMessages(conversationId, { batchCount: nextGroups }, await buildAlexTransport(userId, undefined));
				// A FAILED page leaves `hasOlderMessages` alone rather than clearing it: the control stays available so the
				// reader can retry, instead of the history quietly ending at whatever the outage returned.
				if (!result.success) return;
				setLoadedGroups(nextGroups);
				setHasOlderMessages(hasOlderAfter(nextGroups, result.data.length));
				// Measured HERE, in the last frame before the prepend: the list is still the one the reader is looking at,
				// so this is the only point where the "before" geometry is the truth. The layout effect above consumes it.
				const container = findScrollParent(messagesEndRef.current);
				scrollAnchorRef.current = container ? { scrollHeight: container.scrollHeight, scrollTop: container.scrollTop } : null;
				dispatch({ type: REDUCER_ACTION_TYPES.MESSAGING_SET_MESSAGES, payload: result.data });
			} catch (err: unknown) {
				secureLogger.error(LOG_EVENTS.ASSISTANT_PANE_MESSAGES_FETCH_FAILED, { error: err });
			} finally {
				setLoadingOlderMessages(false);
			}
		})();
	}, [conversationId, userId, loadedGroups, loadingOlderMessages, hasOlderMessages, dispatch, messagesEndRef]);

	return { loadOlderMessages, loadingOlderMessages, hasOlderMessages };
}

/**
 * NO participants read. The pane renders no roster at the user level, and the socket's session-init leg delivers the
 * roster (`onParticipants`) for the join/leave bookkeeping anyway, so an HTTP read would be a verified round trip to
 * the DO whose answer nothing shows.
 */
export function useDataFetching(config: DataFetchingConfig): MessageHistoryPaging {
	useConversationsFetch(config);
	return useMessageHistory(config);
}

export function useConnectionMonitor(
	isConnected: () => boolean,
	state: {
		sendError: string | null;
		setWsConnected: Dispatch<SetStateAction<boolean>>;
		setEverConnected: Dispatch<SetStateAction<boolean>>;
		setSendError: Dispatch<SetStateAction<string | null>>;
	},
): void {
	const { sendError, setWsConnected, setEverConnected, setSendError } = state;
	const connected = isConnected();

	useEffect(() => {
		setWsConnected(connected);
		// Latched, never cleared: it separates the first connect from a drop, which is what the composer's placeholder
		// reads -- "Reconnecting..." on a panel that had never connected was reporting a failure that had not happened.
		if (connected) setEverConnected(true);
	}, [connected, setWsConnected, setEverConnected]);

	const shouldClearError = connected && sendError !== null;
	useEffect(() => {
		if (shouldClearError) {
			setSendError(null);
		}
	}, [shouldClearError, setSendError]);
}

/** Scroll the message list to its bottom -- and NOTHING else. DELIBERATELY NOT `scrollIntoView`: that scrolls EVERY
 *  scrollable ancestor and `block: 'end'` aligns the element's bottom with the scrollport's, which on an outer scroller
 *  drives the composer past the fold. The `scrollIntoView` fallback runs only when no scrollable ancestor exists. */
function scrollToBottom(ref: RefObject<HTMLDivElement | null>): void {
	requestAnimationFrame(() => {
		const container = findScrollParent(ref.current);
		if (container) {
			container.scrollTop = container.scrollHeight;
			return;
		}
		ref.current?.scrollIntoView({ block: 'end', inline: 'nearest' });
	});
}

/** Distance from the bottom, in px, within which we consider the user "pinned". */
const STICK_TO_BOTTOM_SLACK_PX = 80;

/** Nearest scrollable ancestor of the end sentinel, walked UP rather than threaded through AssistantCoreState: the sentinel's own parent is the message wrapper, not the scroll pane. */
function findScrollParent(el: HTMLElement | null): HTMLElement | null {
	for (let node = el?.parentElement ?? null; node; node = node.parentElement) {
		const overflowY = getComputedStyle(node).overflowY;
		if (overflowY === 'auto' || overflowY === 'scroll') return node;
	}
	return null;
}

/** Hold the thread pinned to the bottom while late-loading content grows it: the one-shot `scrollToBottom` fires on a
 *  single animation frame, before any IMAGE has decoded, so it measures a thread hundreds of px shorter than final.
 *  PINNED-NESS IS REMEMBERED, NOT MEASURED ON DEMAND -- content growth does not move `scrollTop`, so `pinned` is
 *  sampled from SCROLL events only. THE TRIGGER IS RESIZE, NOT `load` -- decode is unbounded and `load` misses font
 *  swaps and lazily-rendered cards, so a ResizeObserver reacts to the growth itself. */
function useStickToBottomOnLateContent(messagesEndRef: RefObject<HTMLDivElement | null>, hasMessages: boolean): void {
	useEffect(() => {
		if (!hasMessages) return;
		const container = findScrollParent(messagesEndRef.current);
		if (!container) return;

		let pinned = true;
		const onScroll = () => {
			pinned = container.scrollHeight - container.scrollTop - container.clientHeight <= STICK_TO_BOTTOM_SLACK_PX;
		};
		container.addEventListener('scroll', onScroll, { passive: true });

		const resize = new ResizeObserver(() => {
			if (pinned) scrollToBottom(messagesEndRef);
		});
		const observeChildren = () => {
			resize.disconnect();
			for (const child of Array.from(container.children)) resize.observe(child);
		};
		observeChildren();

		const childList = new MutationObserver(observeChildren);
		childList.observe(container, { childList: true });

		return () => {
			container.removeEventListener('scroll', onScroll);
			resize.disconnect();
			childList.disconnect();
		};
	}, [messagesEndRef, hasMessages]);
}

export function useMessageScrolling(
	displayMessages: ChatMessageStruct[],
	options: {
		messagesEndRef: RefObject<HTMLDivElement | null>;
		hasDisplayedMessagesRef: MutableRefObject<boolean>;
		previousMessageCountRef: MutableRefObject<number>;
		agentStatusActive: boolean;
	},
): void {
	const { messagesEndRef, hasDisplayedMessagesRef, previousMessageCountRef, agentStatusActive } = options;
	// The last message's identity at the previous render. A COUNT alone cannot answer which END of the list grew, and
	// that is the whole question this hook has to get right -- see the auto-scroll effect below.
	const lastMessageIdRef = useRef<string | null>(null);

	// First open with messages.
	useEffect(() => {
		if (displayMessages.length > 0 && !hasDisplayedMessagesRef.current) {
			scrollToBottom(messagesEndRef);
			hasDisplayedMessagesRef.current = true;
			previousMessageCountRef.current = displayMessages.length;
		}
	}, [displayMessages.length, messagesEndRef, hasDisplayedMessagesRef, previousMessageCountRef]);

	/**
	 * Auto-scroll when a message ARRIVES, and never when the reader loads older ones.
	 *
	 * A growing count used to be the whole test, which was right while the list only ever grew at the bottom. Scrollback
	 * breaks that: "Load earlier messages" PREPENDS a group, so the count grows by fifty while nothing arrived -- and
	 * the reader who just asked to see older messages was yanked back to the newest one, undoing the thing they pressed.
	 *
	 * The LAST message's identity is what separates the two, because it is the one thing a prepend cannot change. Count
	 * grew AND the tail is a different message -> something arrived, scroll. Count grew and the tail is the same message
	 * -> the growth was at the top, stay put. A count comparison cannot express that distinction no matter how it is
	 * tuned, which is why the tail id is held rather than derived.
	 */
	useEffect(() => {
		const currentMessageCount = displayMessages.length;
		const currentLastMessageId = displayMessages.at(-1)?.id ?? null;
		const grew = currentMessageCount > previousMessageCountRef.current && previousMessageCountRef.current > 0;

		if (grew && currentLastMessageId !== lastMessageIdRef.current) {
			scrollToBottom(messagesEndRef);
		}

		previousMessageCountRef.current = currentMessageCount;
		lastMessageIdRef.current = currentLastMessageId;
	}, [displayMessages, messagesEndRef, previousMessageCountRef]);

	// The agent status row SHRINKS the messages container, so re-pin the last message above the status strip (mobile has no spare space).
	useEffect(() => {
		if (agentStatusActive) {
			scrollToBottom(messagesEndRef);
		}
	}, [agentStatusActive, messagesEndRef]);

	// A KEEP-ALIVE RE-ATTACH resets the pane. The mobile route re-inserts the live root into its container (after the
	// shell's pre-warm, and on every return), and a scroll container pulled out of the document and put back reads
	// `scrollTop: 0` -- the thread reopened at its OLDEST message (phone, 2026-09-17). Nothing above re-fires, because
	// the message count is unchanged. Keyed to the route's key so the desktop panel does not jump when EXPLORE re-attaches.
	const hasMessages = displayMessages.length > 0;
	useEffect(() => {
		if (!hasMessages) return;
		const onReattach = (event: Event) => {
			if (event instanceof CustomEvent && recordStringOr(event.detail, 'keepAliveKey', '') === ASSISTANT_ROUTE_KEEP_ALIVE_KEY) {
				scrollToBottom(messagesEndRef);
			}
		};
		window.addEventListener(DOM_EVENTS.MFE_HOST_REATTACHED, onReattach);
		return () => window.removeEventListener(DOM_EVENTS.MFE_HOST_REATTACHED, onReattach);
	}, [hasMessages, messagesEndRef]);

	// Images decode AFTER every effect above runs.
	useStickToBottomOnLateContent(messagesEndRef, hasMessages);
}

/** Nested envelope shapes the upload flow response can arrive in. */
type UploadFlowOutput = {
	body?: { data?: Record<string, unknown> };
	data?: Record<string, unknown>;
};
type UploadFlowResult = {
	output?: UploadFlowOutput;
};

function extractDataObject(resultData: Record<string, unknown>): Record<string, unknown> {
	const flowOutput = (resultData as UploadFlowResult).output;
	if (!flowOutput) return resultData;
	const bodyData = flowOutput.body?.data;
	if (bodyData) return bodyData;
	if (flowOutput.data) return flowOutput.data;
	return flowOutput;
}

function extractUploadData(resultData: Record<string, unknown> | undefined, fileName: string, fileType: string): FileUploadMetadata {
	if (!resultData) {
		return { fileId: '', url: '', name: fileName, type: fileType };
	}
	const data = extractDataObject(resultData);
	const fileUrl = recordStringOr(data, 'fileUrl', '') || recordStringOr(data, 'url', '');
	return {
		fileId: recordStringOr(data, 'fileId', ''),
		url: fileUrl,
		name: recordStringOr(data, 'fileName', '') || fileName,
		type: recordStringOr(data, 'fileType', '') || fileType,
	};
}

/** Uploads via the chats worker's own flow-dispatch route, reflecting progress back into selected-file state. REJECTS if any upload fails, so the caller can surface a soft error and KEEP the picked files. */
export async function uploadFiles(params: FileUploadParams): Promise<FileUploadMetadata[]> {
	const { selectedFiles, setSelectedFiles, userId, conversationId } = params;

	secureLogger.info(LOG_EVENTS.ASSISTANT_PANE_ATTACHMENTS_UPLOADING, {
		fileCount: selectedFiles.length,
	});
	setSelectedFiles((prev) => prev.map((file) => ({ ...file, uploading: true, progress: 0 })));

	const updateProgress = (fileId: string, progress: number): void => {
		setSelectedFiles((prev) => prev.map((file) => (file.id === fileId ? { ...file, uploading: progress < 100, progress } : file)));
	};

	// BOUNDED fan-out. The picker's `MAX_ATTACHMENT_FILES` cap lives in AssistantPane.input.tsx, so nothing at this call
	// site bounds `selectedFiles` -- a caller reaching `uploadFiles` another way would burst one POST per file. The
	// window matches that cap, so today's paths still run in a single batch and reject-on-first-failure is unchanged
	// (`mapInBatches` awaits `Promise.all` per window), preserving this function's documented KEEP-the-picked-files contract.
	return mapInBatches(selectedFiles, MAP_IN_BATCHES_DEFAULT_CONCURRENCY, async (fileItem) => {
		updateProgress(fileItem.id, 10);
		const result = await uploadChatFile({
			file: fileItem.file,
			userId,
			conversationId,
			onProgress: (progress) => updateProgress(fileItem.id, progress),
		});
		// Split guard: the result is literal-tagged, so `message` lives on the failure branch ONLY; a success without a payload falls to the generic string.
		if (!result.success) throw new Error(result.message);
		if (!result.data) throw new Error('Upload failed');
		updateProgress(fileItem.id, 100);
		return extractUploadData(result.data, fileItem.file.name, fileItem.file.type);
	});
}
